import React, { useCallback, useEffect, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';

interface CurrencyInputProps {
  value: number; // Value in cents
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  prefix?: string;
  min?: number;
  max?: number;
}

/**
 * CurrencyInput component that handles currency input with proper formatting
 * Value is stored in cents internally but displayed in currency format (RM X.XX)
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value = 0,
  onChange,
  placeholder = '0.00',
  disabled = false,
  error,
  helperText,
  className = '',
  prefix = 'RM',
  min = 0,
  max = 999999999,
}) => {
  // Format cents to display value (e.g., 1050 -> "10.50")
  const formatCentsToDisplay = (cents: number): string => {
    const amount = Math.max(0, Math.min(cents, max));
    return (amount / 100).toFixed(2);
  };

  // Local state for the display value
  const [displayValue, setDisplayValue] = useState(() => formatCentsToDisplay(value));
  const [isFocused, setIsFocused] = useState(false);

  // Update display when value prop changes (from parent)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatCentsToDisplay(value));
    }
  }, [value, isFocused]);

  // Handle keyboard input for better UX
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace/delete specially for currency input
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      
      // Remove last digit by dividing by 10 and flooring
      const newCents = Math.floor(value / 10);
      
      // Apply min/max constraints
      const constrainedValue = Math.max(min, Math.min(newCents, max));
      onChange(constrainedValue);
      setDisplayValue(formatCentsToDisplay(constrainedValue));
    }
  };

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Extract only digits from the input
    const digitsOnly = inputValue.replace(/[^\d]/g, '');
    const prevDigitsOnly = displayValue.replace(/[^\d]/g, '');
    
    // If user is typing new digits
    if (digitsOnly.length > prevDigitsOnly.length) {
      // Get only the newly typed digits
      const newDigits = digitsOnly.slice(prevDigitsOnly.length);
      
      // Calculate new value: multiply old value by 10^(number of new digits) and add new digits
      let newCents = value;
      for (const digit of newDigits) {
        newCents = newCents * 10 + parseInt(digit, 10);
        
        // Apply max constraint
        if (newCents > max) {
          newCents = max;
          break;
        }
      }
      
      // Apply min constraint
      newCents = Math.max(min, newCents);
      
      onChange(newCents);
      setDisplayValue(formatCentsToDisplay(newCents));
    }
  };

  // Handle focus events
  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Select all text on focus for easy replacement
    e.target.select();
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Ensure display is properly formatted on blur
    setDisplayValue(formatCentsToDisplay(value));
  }, [value]);

  return (
    <div className="w-full">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
          {prefix}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-12 pr-3 py-2.5 
            text-lg font-medium
            border-2 rounded-lg 
            transition-all duration-200
            ${error 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
              : 'border-blue-400 focus:border-blue-500 focus:ring-blue-200'
            }
            ${disabled 
              ? 'bg-gray-100 cursor-not-allowed opacity-60' 
              : 'bg-white hover:border-blue-500'
            }
            focus:outline-none focus:ring-4
            ${className}
          `}
          style={{
            fontSize: '16px',
            lineHeight: '24px',
          }}
        />
      </div>
      
      {/* Helper text or error message */}
      {helperText && !error && (
        <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
          <FaInfoCircle className="text-gray-400" />
          {helperText}
        </p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <FaInfoCircle />
          {error}
        </p>
      )}
    </div>
  );
};

export default CurrencyInput;